import type { DetectionEvent } from "@/types";
import type { Explosion, Trajectory } from "@/types/geolocation";

const DB_NAME = "pushka-db";
// v4: adds the `audioClips` store (local "listen back" clips for own detections). The version
// bump forces `onupgradeneeded`; `createStores` is idempotent and self-heals stale schemas.
const DB_VERSION = 4;
const STORE_NAME = "events";
const EXPLOSION_STORE = "explosions";
const TRAJECTORY_STORE = "trajectories";
const AUDIO_CLIP_STORE = "audioClips";
/** Every object store the app relies on — used to detect a stale schema on open. */
const REQUIRED_STORES = [STORE_NAME, EXPLOSION_STORE, TRAJECTORY_STORE, AUDIO_CLIP_STORE];
/** Own-detections history: kept for 6 hours; the count cap is generous so time is the real bound. */
const MAX_EVENTS = 500;
const EVENT_TTL_MINUTES = 6 * 60;
/** Localisation results live longer than raw events — they are the map's offline state. */
const RESULT_TTL_MINUTES = 60;
const MAX_RESULTS = 200;
/** Raw "listen back" audio clips: heavy, so kept only briefly (≤1 h) and only the 5 newest. */
const AUDIO_CLIP_TTL_MINUTES = 60;
const MAX_AUDIO_CLIPS = 5;

export class IndexedDBStore {
  private db: IDBDatabase | null = null;

  /** Create any object store that doesn't yet exist. Idempotent — safe across schema versions. */
  private createStores(db: IDBDatabase): void {
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      const store = db.createObjectStore(STORE_NAME, {
        keyPath: "id",
        autoIncrement: true,
      });
      store.createIndex("timestamp", "timestamp", { unique: false });
      store.createIndex("synced", "synced", { unique: false });
      store.createIndex("clusterId", "clusterId", { unique: false });
    }
    // v2: cached localisation results (the offline map state).
    if (!db.objectStoreNames.contains(EXPLOSION_STORE)) {
      const store = db.createObjectStore(EXPLOSION_STORE, { keyPath: "id" });
      store.createIndex("cachedAt", "cachedAt", { unique: false });
    }
    if (!db.objectStoreNames.contains(TRAJECTORY_STORE)) {
      const store = db.createObjectStore(TRAJECTORY_STORE, { keyPath: "id" });
      store.createIndex("cachedAt", "cachedAt", { unique: false });
    }
    // v4: local raw-audio clips keyed by the detection event's id (for in-app "listen back").
    if (!db.objectStoreNames.contains(AUDIO_CLIP_STORE)) {
      const store = db.createObjectStore(AUDIO_CLIP_STORE, { keyPath: "eventId" });
      store.createIndex("cachedAt", "cachedAt", { unique: false });
    }
  }

  async open(): Promise<void> {
    if (this.db) return;
    return new Promise((resolve, reject) => {
      let settled = false;
      const done = (cb: () => void): void => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        cb();
      };
      // A version upgrade can be *blocked* by another tab/connection holding the DB open at an
      // older version. Without this guard the promise stays pending forever and freezes the
      // caller (onMounted), so the map never renders. Reject after a short timeout instead.
      const timer = setTimeout(() => {
        done(() =>
          reject(new Error("IndexedDB не открылась вовремя (возможно, открыта в другой вкладке)")),
        );
      }, 4000);

      let request: IDBOpenDBRequest;
      try {
        request = indexedDB.open(DB_NAME, DB_VERSION);
      } catch (e) {
        done(() => reject(e instanceof Error ? e : new Error("Не удалось открыть IndexedDB")));
        return;
      }

      request.onerror = () => done(() => reject(new Error("Не удалось открыть IndexedDB")));
      // Fired when our upgrade is held up by another open connection; the timeout above rejects.
      request.onblocked = () => {};

      request.onsuccess = () => {
        const db = request.result;
        // If another tab later requests a newer version, close so we don't block its upgrade.
        db.onversionchange = () => db.close();

        // Self-heal: if an older DB is missing a store the app needs (e.g. a stale v2 created
        // before the result caches existed), reopen at the next version to force an upgrade.
        const missing = REQUIRED_STORES.filter(
          (name) => !db.objectStoreNames.contains(name),
        );
        if (missing.length > 0) {
          const nextVersion = db.version + 1;
          db.close();
          const upgrade = indexedDB.open(DB_NAME, nextVersion);
          upgrade.onupgradeneeded = (event) => {
            this.createStores((event.target as IDBOpenDBRequest).result);
          };
          upgrade.onblocked = () => {};
          upgrade.onsuccess = () => {
            done(() => {
              this.db = upgrade.result;
              this.db.onversionchange = () => this.db?.close();
              resolve();
            });
          };
          upgrade.onerror = () => {
            done(() => reject(new Error("Не удалось обновить схему IndexedDB")));
          };
          return;
        }
        done(() => {
          this.db = db;
          resolve();
        });
      };

      request.onupgradeneeded = (event) => {
        this.createStores((event.target as IDBOpenDBRequest).result);
      };
    });
  }

  async saveEvent(event: DetectionEvent): Promise<number> {
    if (!this.db) {
      await this.open();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.add({
        ...event,
        createdAt: Date.now(),
      });

      request.onsuccess = () => {
        const id = request.result as number;
        this.cleanup().catch(console.error);
        resolve(id);
      };

      request.onerror = () => {
        reject(new Error("Не удалось сохранить событие"));
      };
    });
  }

  async getUnsyncedEvents(): Promise<DetectionEvent[]> {
    if (!this.db) {
      await this.open();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        const allEvents = request.result as DetectionEvent[];
        const unsynced = allEvents.filter((event) => !event.synced);
        resolve(unsynced);
      };

      request.onerror = () => {
        reject(new Error("Не удалось получить события"));
      };
    });
  }

  async getRecentEvents(minutes: number = 20): Promise<DetectionEvent[]> {
    if (!this.db) {
      await this.open();
    }

    const cutoff = Date.now() - minutes * 60 * 1000;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index("timestamp");
      const request = index.openCursor(IDBKeyRange.lowerBound(cutoff));

      const events: DetectionEvent[] = [];

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest)
          .result as IDBCursorWithValue;
        if (cursor) {
          events.push(cursor.value as DetectionEvent);
          cursor.continue();
        } else {
          resolve(events);
        }
      };

      request.onerror = () => {
        reject(new Error("Не удалось получить события"));
      };
    });
  }

  async getAllEvents(): Promise<DetectionEvent[]> {
    if (!this.db) {
      await this.open();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve((request.result as DetectionEvent[]).reverse());
      };

      request.onerror = () => {
        reject(new Error("Не удалось получить события"));
      };
    });
  }

  async markAsSynced(ids: number[]): Promise<void> {
    if (!this.db) {
      await this.open();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);

      let completed = 0;
      const total = ids.length;

      ids.forEach((id) => {
        const getRequest = store.get(id);

        getRequest.onsuccess = () => {
          const event = getRequest.result as DetectionEvent;
          if (event) {
            event.synced = true;
            const putRequest = store.put(event);

            putRequest.onsuccess = () => {
              completed++;
              if (completed === total) {
                resolve();
              }
            };

            putRequest.onerror = () => {
              reject(new Error("Не удалось обновить событие"));
            };
          } else {
            completed++;
            if (completed === total) {
              resolve();
            }
          }
        };

        getRequest.onerror = () => {
          reject(new Error("Не удалось получить событие"));
        };
      });
    });
  }

  async deleteEvent(id: number): Promise<void> {
    if (!this.db) {
      await this.open();
    }

    return new Promise((resolve, reject) => {
      // Drop the event and its local audio clip (if any) in one transaction.
      const transaction = this.db!.transaction([STORE_NAME, AUDIO_CLIP_STORE], "readwrite");
      transaction.objectStore(STORE_NAME).delete(id);
      transaction.objectStore(AUDIO_CLIP_STORE).delete(id);

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(new Error("Не удалось удалить событие"));
    });
  }

  async updateEventCluster(eventId: number, clusterId: string): Promise<void> {
    if (!this.db) {
      await this.open();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const getRequest = store.get(eventId);

      getRequest.onsuccess = () => {
        const event = getRequest.result as DetectionEvent;
        if (event) {
          event.clusterId = clusterId;
          const putRequest = store.put(event);

          putRequest.onsuccess = () => {
            resolve();
          };

          putRequest.onerror = () => {
            reject(new Error("Не удалось обновить кластер события"));
          };
        } else {
          reject(new Error("Событие не найдено"));
        }
      };

      getRequest.onerror = () => {
        reject(new Error("Не удалось получить событие"));
      };
    });
  }

  /**
   * Обновить постоянный статус отправки события (syncStatus/syncError) и флаг synced.
   * Schemaless по полям объекта — новые поля дописываются в существующую запись без миграции.
   */
  async updateSyncStatus(
    eventId: number,
    status: 'pending' | 'sent' | 'no_location' | 'error',
    error: string | null,
  ): Promise<void> {
    if (!this.db) {
      await this.open();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const getRequest = store.get(eventId);

      getRequest.onsuccess = () => {
        const event = getRequest.result as DetectionEvent;
        if (!event) {
          reject(new Error('Событие не найдено'));
          return;
        }
        event.syncStatus = status;
        event.syncError = error;
        event.synced = status === 'sent';
        const putRequest = store.put(event);

        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () =>
          reject(new Error('Не удалось обновить статус отправки события'));
      };

      getRequest.onerror = () => reject(new Error('Не удалось получить событие'));
    });
  }

  /**
   * Сохранить объект глубокой диагностики события (diagnostics) — schemaless по структуре:
   * новые поля (clipTrace и т.п.) дописываются в существующую запись без миграции схемы.
   * Используется фичей «Графики отладки» для локального кэша per-window трейса каскада.
   */
  async updateEventDiagnostics(
    eventId: number,
    diagnostics: NonNullable<DetectionEvent['diagnostics']>,
  ): Promise<void> {
    if (!this.db) {
      await this.open();
    }
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const getRequest = store.get(eventId);
      getRequest.onsuccess = () => {
        const event = getRequest.result as DetectionEvent;
        if (!event) {
          reject(new Error('Событие не найдено'));
          return;
        }
        event.diagnostics = diagnostics;
        const putRequest = store.put(event);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () =>
          reject(new Error('Не удалось сохранить диагностику события'));
      };
      getRequest.onerror = () => reject(new Error('Не удалось получить событие'));
    });
  }

  async cleanup(): Promise<void> {
    if (!this.db) {
      await this.open();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index("timestamp");

      const cutoff = Date.now() - EVENT_TTL_MINUTES * 60 * 1000;
      const request = index.openCursor(IDBKeyRange.upperBound(cutoff));

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest)
          .result as IDBCursorWithValue;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          this.limitEvents(MAX_EVENTS).then(() => resolve());
        }
      };

      request.onerror = () => {
        reject(new Error("Не удалось выполнить очистку"));
      };
    });
  }

  private async limitEvents(maxEvents: number): Promise<void> {
    if (!this.db) {
      await this.open();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        const events = request.result as DetectionEvent[];
        if (events.length <= maxEvents) {
          resolve();
          return;
        }

        const sorted = events.sort((a, b) => a.timestamp - b.timestamp);
        const toDelete = sorted.slice(0, sorted.length - maxEvents);

        let deleted = 0;
        toDelete.forEach((event) => {
          if (event.id !== undefined) {
            const deleteRequest = store.delete(event.id);
            deleteRequest.onsuccess = () => {
              deleted++;
              if (deleted === toDelete.length) {
                resolve();
              }
            };
          }
        });
      };

      request.onerror = () => {
        reject(new Error("Не удалось ограничить количество событий"));
      };
    });
  }

  // === v2: localisation result cache (explosions + trajectories) ===

  async saveExplosions(items: Explosion[]): Promise<void> {
    return this.upsertResults(EXPLOSION_STORE, items as Array<{ id: string }>);
  }

  async saveTrajectories(items: Trajectory[]): Promise<void> {
    return this.upsertResults(TRAJECTORY_STORE, items as Array<{ id: string }>);
  }

  async getCachedExplosions(): Promise<Explosion[]> {
    return this.readResults<Explosion>(EXPLOSION_STORE);
  }

  async getCachedTrajectories(): Promise<Trajectory[]> {
    return this.readResults<Trajectory>(TRAJECTORY_STORE);
  }

  /** Upsert results by `id`, stamping `cachedAt`, then prune by TTL + size cap. */
  private async upsertResults(
    storeName: string,
    items: Array<{ id: string }>,
  ): Promise<void> {
    if (!this.db) await this.open();
    if (items.length === 0) return;

    await new Promise<void>((resolve, reject) => {
      const tx = this.db!.transaction([storeName], "readwrite");
      const store = tx.objectStore(storeName);
      const now = Date.now();
      for (const item of items) {
        if (!item.id) continue;
        store.put({ ...item, cachedAt: now });
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(new Error("Не удалось сохранить результаты в кэш"));
    });

    await this.pruneResults(storeName);
  }

  private async readResults<T>(storeName: string): Promise<T[]> {
    if (!this.db) await this.open();
    const cutoff = Date.now() - RESULT_TTL_MINUTES * 60 * 1000;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction([storeName], "readonly");
      const store = tx.objectStore(storeName);
      const request = store.getAll();
      request.onsuccess = () => {
        const rows = (request.result as Array<T & { cachedAt: number }>)
          .filter((r) => r.cachedAt >= cutoff)
          .sort((a, b) => b.cachedAt - a.cachedAt);
        resolve(
          rows.map((r) => {
            const copy: Record<string, unknown> = { ...r };
            delete copy.cachedAt;
            return copy as unknown as T;
          }),
        );
      };
      request.onerror = () => reject(new Error("Не удалось прочитать кэш результатов"));
    });
  }

  /** Drop TTL-expired rows, then trim to MAX_RESULTS newest. */
  private async pruneResults(storeName: string): Promise<void> {
    if (!this.db) await this.open();
    const cutoff = Date.now() - RESULT_TTL_MINUTES * 60 * 1000;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction([storeName], "readwrite");
      const store = tx.objectStore(storeName);
      const request = store.getAll();
      request.onsuccess = () => {
        const rows = request.result as Array<{ id: string; cachedAt: number }>;
        const fresh = rows
          .filter((r) => r.cachedAt >= cutoff)
          .sort((a, b) => b.cachedAt - a.cachedAt);
        const keep = new Set(fresh.slice(0, MAX_RESULTS).map((r) => r.id));
        for (const r of rows) {
          if (!keep.has(r.id)) store.delete(r.id);
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(new Error("Не удалось очистить кэш результатов"));
    });
  }

  // === v4: local raw-audio clips (own-detections "listen back") ===

  /**
   * Store a raw-audio clip for a detection event, then prune so only the freshest few survive.
   * PRIVACY: clips are local-only (never uploaded) and short-lived (see AUDIO_CLIP_* limits).
   */
  async saveAudioClip(eventId: number, wav: Blob): Promise<void> {
    if (!this.db) await this.open();

    await new Promise<void>((resolve, reject) => {
      const tx = this.db!.transaction([AUDIO_CLIP_STORE], "readwrite");
      tx.objectStore(AUDIO_CLIP_STORE).put({ eventId, wav, cachedAt: Date.now() });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(new Error("Не удалось сохранить аудиозапись"));
    });

    await this.pruneAudioClips();
  }

  /** Read the stored clip for an event, or null if none (expired / pruned / never captured). */
  async getAudioClip(eventId: number): Promise<Blob | null> {
    if (!this.db) await this.open();
    const cutoff = Date.now() - AUDIO_CLIP_TTL_MINUTES * 60 * 1000;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction([AUDIO_CLIP_STORE], "readonly");
      const request = tx.objectStore(AUDIO_CLIP_STORE).get(eventId);
      request.onsuccess = () => {
        const row = request.result as { wav: Blob; cachedAt: number } | undefined;
        if (!row || row.cachedAt < cutoff) {
          resolve(null);
          return;
        }
        resolve(row.wav);
      };
      request.onerror = () => reject(new Error("Не удалось прочитать аудиозапись"));
    });
  }

  /** Keep only clips that are both within the TTL window AND among the MAX_AUDIO_CLIPS newest. */
  private async pruneAudioClips(): Promise<void> {
    if (!this.db) await this.open();
    const cutoff = Date.now() - AUDIO_CLIP_TTL_MINUTES * 60 * 1000;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction([AUDIO_CLIP_STORE], "readwrite");
      const store = tx.objectStore(AUDIO_CLIP_STORE);
      const request = store.getAll();
      request.onsuccess = () => {
        const rows = request.result as Array<{ eventId: number; cachedAt: number }>;
        const fresh = rows
          .filter((r) => r.cachedAt >= cutoff)
          .sort((a, b) => b.cachedAt - a.cachedAt);
        const keep = new Set(fresh.slice(0, MAX_AUDIO_CLIPS).map((r) => r.eventId));
        for (const r of rows) {
          if (!keep.has(r.eventId)) store.delete(r.eventId);
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(new Error("Не удалось очистить аудиозаписи"));
    });
  }

  clearAll(): Promise<void> {
    if (!this.db) {
      return this.open().then(() => this.clearAll());
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME, AUDIO_CLIP_STORE], "readwrite");
      transaction.objectStore(STORE_NAME).clear();
      transaction.objectStore(AUDIO_CLIP_STORE).clear();

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(new Error("Не удалось очистить базу данных"));
    });
  }
}
